// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { AGE_UP_THRESHOLD } from "./age.js";
import { MIN_REWARD_TOKENS, STARTER_HABITS_OLDER, STARTER_HABITS_YOUNG, STARTER_TASKS_OLDER, STARTER_TASKS_YOUNG, uid } from "./constants.js";
import { capTokensFor, distributeTaskSlot, splitForCap } from "./earnSplit.js";
import { cmsStarterSet } from "./content.js";
import { spendingCapPence, scaleRewardForRateChange } from "./money.js";

/* Starter tasks, seeded per child and priced from that child's own cap.
 *
 * MUTATES the state passed in — call from inside update(), or on a draft during migration.
 * Idempotent per child: seeds only when the child has no tasks at all, so a parent who
 * deliberately deleted both starters never has them reappear.
 *
 * The split maths is duplicated from spendingDefaults.js rather than imported, because
 * utils.js is imported BY that module — importing back would make the cycle real. Both
 * read their shares from EARN_SPLIT in constants.js, so there is still exactly one place
 * to change the balance.
 */
// The standard sets a child is seeded from, for one age band (LB-131). CMS entries in
// the `starter-set` area win when published and well-formed; the constants in
// constants.js are the baked fallback, and scripts/check-starter-sets.mjs asserts the two
// are identical at every commit so a fresh environment behaves exactly like a seeded one.
//
// Resolved at SEED time, not module load: the bundle arrives asynchronously after boot,
// and a household created in the first seconds of a first-ever visit is seeded before
// it lands. That child gets the baked set — the same words, by the drift check above —
// which is the honest reason the check exists rather than a nicety.
export function resolveStarterSets(band) {
  const young = band === "young";
  const tasks = cmsStarterSet("tasks", band) || (young ? STARTER_TASKS_YOUNG : STARTER_TASKS_OLDER);
  const habitItems = cmsStarterSet("habits", band);
  const habits = habitItems ? habitItems.map((h) => h.label) : (young ? STARTER_HABITS_YOUNG : STARTER_HABITS_OLDER);
  return { tasks, habits };
}

// Seeds a child's STANDARD SET and prices every part of it from that child's own cap.
//
// LB-132, 31 Aug. What this replaced seeded two reading tasks, priced only those from the
// cap, seeded no habits at all, and left the Learn & Earn rewards on flat defaults set by
// seedNewChildDefaults. Measured result: a fresh child at a £5 cap could reach 40% of it, at
// £10 only 30% — the shortfall grew as the cap rose, because the flat L&E values never moved.
// A parent set an allowance and the app quietly configured a child who could not earn it.
//
// The split was always there. EARN_SPLIT is applied by suggestedValues and repriceToCap —
// the wizard OFFERS it and the Spending review OFFERS it — but nothing applied it at seed
// time. So the split described what the app recommended and not what it did.
//
// Now: standard set in, cap split four ways, every value derived. A busy week on a freshly
// seeded child lands on the cap exactly, which is what makes the auto-setup review screen
// worth accepting rather than an admission that 60% of the allowance is unreachable.
export function seedStandardSet(state, childId) {
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (!child) return false;
  if (!Array.isArray(state.activities)) state.activities = [];
  if (!Array.isArray(state.habits)) state.habits = [];
  // Idempotent on tasks, as before: a child that already has any is left completely alone.
  if (state.activities.some((a) => a.childId === childId)) return false;

  const young = (child.age ?? AGE_UP_THRESHOLD) < AGE_UP_THRESHOLD;
  const { tasks: fullSet, habits } = resolveStarterSets(young ? "young" : "older");

  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const lc = (state.learningContent || {})[childId] || {};
  const payouts = { english: Math.max(1, lc.learnEarnPayoutsPerWeek || 1), maths: Math.max(1, lc.mathsPayoutsPerWeek || 1) };
  const split = splitForCap(capTokensFor(child, rate), payouts);

  // THE CAP DECIDES HOW MUCH OF THE SET A CHILD GETS. Four tasks at the MIN_REWARD_TOKENS
  // floor cost 8 tokens; a £3 cap only has a 6-token task slot, so seeding the whole set
  // would put a brand-new child OVER their cap and open the review screen on a warning.
  // Seeding fewer, heavier tasks is the honest resolution: the set is standard, the cap says
  // how much of it is affordable. Heaviest weights first, so what survives is the
  // substantial chores rather than whichever happened to be listed first.
  // Weights are re-normalised over whichever tasks survive the cut below, so a £3 child's
  // two tasks split the whole task slot between them rather than 60% of it. The baked sets
  // sum to 1 already; a CMS set (LB-131) is under no obligation to, and an operator
  // adjusting one weight should not have to re-balance the other three by hand.
  const normalise = (set) => {
    const total = set.reduce((a, t) => a + t.weight, 0) || 1;
    return set.map((t) => ({ label: t.label, weight: t.weight / total }));
  };
  const byWeight = fullSet.slice().sort((a, b) => b.weight - a.weight);
  let tasks = normalise(fullSet);
  // Cap off entirely: there is no cap to derive from, so seed the whole set unpriced-by-cap
  // at the floor. A household that turned the guardrail off has said it does not want one.
  let amounts = tasks.map(() => MIN_REWARD_TOKENS * 2);
  if (split) {
    // Try the whole set first, then drop the LIGHTEST task and retry until the priced set
    // fits the slot. Deciding by the priced result rather than by slot ÷ floor is what
    // makes this hold for any weights: rounding plus the reward floor can push a set over
    // even when the count looked affordable (three tasks at 0.44/0.31/0.25 of a 6-token
    // slot price to 3+2+2 = 7).
    for (let n = fullSet.length; n >= 1; n--) {
      const candidate = normalise(n >= fullSet.length ? fullSet : byWeight.slice(0, n));
      const priced = distributeTaskSlot(split.taskSlot, candidate.map((t) => t.weight));
      tasks = candidate;
      amounts = priced;
      if (priced.reduce((a, b) => a + b, 0) <= split.taskSlot || n === 1) break;
    }
  }
  // `seeded` records what the seeder wrote, so a later re-seed (LB-127: the cap changes
  // before setup is confirmed) can tell an untouched standard row from one the parent has
  // renamed or repriced. A row whose label and tokens still equal its stamp is the app's;
  // anything else is the parent's and is never rewritten by seeding again.
  tasks.forEach((t, i) => {
    const tokens = Math.max(MIN_REWARD_TOKENS, amounts[i]);
    state.activities.push({ id: uid(), label: t.label, tokens, childId, linkKey: null, seeded: { label: t.label, tokens } });
  });

  // Habits carry no individual price — the streak reward is one amount for completing the
  // whole set — so a longer list makes the streak harder, never more expensive.
  if (!state.habits.some((h) => h.childId === childId)) {
    habits.forEach((label) => state.habits.push({ id: uid(), label, childId, seeded: { label } }));
  }

  if (split) {
    stampSeededRewards(state, child, split);
  }
  child._seededFor = seedKeyFor(child, rate);
  child._seededLabels = tasks.map((t) => t.label);
  return true;
}

// What the current seed was derived FROM. reseedStandardSetFromCap re-seeds only when this
// moves — a parent deleting a pre-staged task must not see it come back on the next visit
// to the step (guardrails scope: starters never reappear once deleted).
function seedKeyFor(child, rate) {
  return `${spendingCapPence(child) ?? "off"}|${rate}|${(child.age ?? AGE_UP_THRESHOLD) < AGE_UP_THRESHOLD ? "young" : "older"}`;
}

// Writes the cap-derived reward values and remembers them (`_seededRewards`), so a re-seed
// can leave alone any value the parent has since changed. Same shape applySuggestedValues
// stamps, so seeded and repriced children are indistinguishable: full rewards at the
// per-payout figure, passes at 60% of it.
export function stampSeededRewards(state, child, split, onlyUntouched = false) {
  const pass = (n) => Math.max(MIN_REWARD_TOKENS, Math.round(n * 0.6));
  const target = (state.learningContent || {})[child.id];
  const prev = (target && target._seededRewards) || {};
  const untouched = (obj, key) => !onlyUntouched || prev[key] === undefined || obj[key] === prev[key];
  const next = {};
  if (untouched(child, "habitReward")) { child.habitReward = split.habit; }
  next.habitReward = split.habit;
  if (target) {
    const set = (key, value) => { if (untouched(target, key)) target[key] = value; next[key] = value; };
    // LB-283: and the same gap on the other side. Every other game re-prices BOTH its rewards
    // when the weekly cap moves; spelling only re-priced the full one, so its pass reward
    // drifted against its own full reward every time the cap changed. That half is an ongoing
    // bug rather than a setup-time one, and the more important of the two.
    if (target.spellingEnabled !== false) { set("spellingReward", split.englishPerPayout); set("spellingPassReward", pass(split.englishPerPayout)); }
    if (target.vocabEnabled !== false) { set("vocabFullReward", split.englishPerPayout); set("vocabPassReward", pass(split.englishPerPayout)); }
    if (target.ttstreaksEnabled !== false) { set("ttStreakFullReward", split.mathsPerPayout); set("ttStreakPassReward", pass(split.mathsPerPayout)); }
    if (target.mathsworkoutEnabled !== false) { set("mathsFullReward", split.mathsPerPayout); set("mathsPassReward", pass(split.mathsPerPayout)); }
    target._seededRewards = next;
  }
}

// Exported for the add-a-child-later flow (LB-129): switching a new child's seed source
// (sibling ↔ standard set) must drop only the rows the app wrote, never the parent's.
export function removeUntouchedSeeds(state, childId) {
  state.activities = (state.activities || []).filter((a) => !(a.childId === childId && isUntouchedSeed(a)));
  state.habits = (state.habits || []).filter((h) => !(h.childId === childId && isUntouchedSeed(h)));
}
export function isUntouchedSeed(row) {
  const s = row && row.seeded;
  if (!s || typeof s !== "object") return false;
  if (s.label !== row.label) return false;
  return s.tokens === undefined || s.tokens === row.tokens;
}

// Re-derives a child's standard set from their CURRENT cap, touching nothing the parent
// has changed (LB-127 rider 4, LB-128). Rows still carrying their seed stamp are removed
// and re-seeded; renamed or repriced rows, rows the parent added, and reward values the
// parent edited all stay exactly as they are. Idempotent: when the cap has not moved, the
// re-seeded rows come out identical and the existing ones are kept (same ids).
//
// This is what runs at the setup fork and again when a cap is changed on the review
// screen. It is deliberately NOT applySuggestedValues, which reprices every row equally
// and would silently overwrite a parent's own numbers — the exact failure the guardrails
// scope forbids. If the parent's own rows put the child over cap, the health line says so
// and Reprice is their tap.
export function reseedStandardSetFromCap(state, childId) {
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (!child) return false;
  if (!Array.isArray(state.activities)) state.activities = [];
  if (!Array.isArray(state.habits)) state.habits = [];
  const rateNow = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const hasAny = state.activities.some((a) => a.childId === childId) || state.habits.some((h) => h.childId === childId);
  if (!hasAny) return seedStandardSet(state, childId);
  if (child._seededFor === seedKeyFor(child, rateNow)) return false;
  const mine = (rows) => rows.filter((r) => r.childId === childId);
  const keptTasks = mine(state.activities).filter((a) => !isUntouchedSeed(a));
  const keptHabits = mine(state.habits).filter((h) => !isUntouchedSeed(h));
  const oldSeedTasks = mine(state.activities).filter(isUntouchedSeed);
  const oldSeedHabits = mine(state.habits).filter(isUntouchedSeed);

  // Dry run on a scratch copy — empty of this child's rows, so the seeder (idempotent on
  // "a child with any task is left alone") produces the full standard set — and compare it
  // with what the untouched rows currently are. An unchanged cap yields an identical set,
  // and the real rows (with their ids) are then left alone.
  const scratch = { ...state, activities: [], habits: [], profiles: state.profiles.map((p) => ({ ...p })), learningContent: JSON.parse(JSON.stringify(state.learningContent || {})) };
  seedStandardSet(scratch, childId);
  let newSeedTasks = scratch.activities.filter((a) => a.childId === childId);
  const newSeedHabits = scratch.habits.filter((h) => h.childId === childId);
  // A parent who removed a pre-staged task has made a decision; a re-seed must not undo
  // it. If any originally seeded task is gone (rather than renamed or repriced — those
  // still carry their stamp), the regenerated set is limited to the labels that survived.
  const originally = Array.isArray(child._seededLabels) ? child._seededLabels : null;
  if (originally) {
    const survivors = mine(state.activities).filter((a) => a.seeded && originally.includes(a.seeded.label)).map((a) => a.seeded.label);
    if (survivors.length < originally.length) {
      const keepLabels = new Set(oldSeedTasks.map((a) => a.seeded.label));
      newSeedTasks = newSeedTasks.filter((a) => keepLabels.has(a.label));
    }
  }
  const sig = (rows) => rows.map((r) => `${r.label}|${r.tokens ?? ""}`).join("\n");
  const tasksSame = sig(oldSeedTasks) === sig(newSeedTasks);
  const habitsSame = sig(oldSeedHabits) === sig(newSeedHabits);

  if (!tasksSame) {
    state.activities = state.activities.filter((a) => !(a.childId === childId && isUntouchedSeed(a)));
    newSeedTasks.forEach((t) => state.activities.push(t));
  }
  if (!habitsSame) {
    state.habits = state.habits.filter((h) => !(h.childId === childId && isUntouchedSeed(h)));
    newSeedHabits.forEach((h) => state.habits.push(h));
  }
  // Rewards: untouched values follow the cap; edited ones stay.
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const lc = (state.learningContent || {})[childId] || {};
  const payouts = { english: Math.max(1, lc.learnEarnPayoutsPerWeek || 1), maths: Math.max(1, lc.mathsPayoutsPerWeek || 1) };
  const split = splitForCap(capTokensFor(child, rate), payouts);
  if (split) stampSeededRewards(state, child, split, true);
  child._seededFor = seedKeyFor(child, rate);
  if (!originally) child._seededLabels = newSeedTasks.map((a) => a.label);
  return true;
}

// Kept so nothing that imported the old name breaks at the seam.
export const seedStarterTasks = seedStandardSet;

// The exchange rate every hardcoded reward default in this file (and migrateState's
// backfills above) is tuned against — 10 tokens/£, i.e. defaultState()'s own rate.
export const BASE_EXCHANGE_RATE = 10;

/* W1.2 gap fix (Aug 2026). A child added to an EXISTING household (ProfilesTab's add
 * form, outside the onboarding wizard) used to get their per-child reward defaults via
 * migrateState's undefined-backfill on the NEXT load — the raw hardcoded values above,
 * tuned for the base rate of 10, regardless of the household's actual exchange rate. A
 * 5:1 household's new child therefore started with rewards holding double the £ value of
 * their siblings' (the reported bug), and a 20:1 household's new child half. The
 * onboarding wizard had the same mechanism under it: children are added before the rate
 * step, their learningContent reward keys don't exist yet when TokenSetupStep's
 * rescaleHouseholdRewards runs (typeof-number guards skip them), and habitReward was
 * undefined until a UI fallback supplied an unscaled 20.
 *
 * Fix: seed EXPLICIT values at add time —
 *   - habitReward/habitThreshold from the household defaults (settings.defaultHabitReward
 *     / defaultHabitThreshold, which W1.2's onboarding rescale already keeps in real-£
 *     terms, and which "apply to all" pushes to every child anyway);
 *   - the seven Learn & Earn reward keys at their base defaults scaled by
 *     exchangeRate / BASE_EXCHANGE_RATE.
 * Explicit values mean migrateState's `=== undefined` backfills never fire for this
 * child, and — because they're now real numbers — any LATER rate change that reruns
 * rescaleHouseholdRewards scales this child correctly along with everyone else (which is
 * also what makes the mid-wizard add order-independent: a child added at rate 10 is
 * seeded at ratio 1, then scaled by the step-3 rescale exactly like their siblings).
 * Non-reward defaults (test sizes, thresholds, tables, enabled flags) are deliberately
 * left to migrateState — they aren't money-denominated, so the base values are already
 * right at any rate.
 */
export function seedNewChildDefaults(state, childId) {
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : BASE_EXCHANGE_RATE;
  const ratio = rate / BASE_EXCHANGE_RATE;
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (child) {
    if (child.habitReward === undefined) child.habitReward = state.settings.defaultHabitReward ?? scaleRewardForRateChange(20, ratio);
    if (child.habitThreshold === undefined) child.habitThreshold = state.settings.defaultHabitThreshold ?? 7;
  }
  if (!state.learningContent || typeof state.learningContent !== "object") state.learningContent = {};
  if (!state.learningContent[childId]) state.learningContent[childId] = { spellingWords: [], vocabBank: [] };
  const lc = state.learningContent[childId];
  const seed = (key, base) => { if (lc[key] === undefined) lc[key] = scaleRewardForRateChange(base, ratio); };
  seed("spellingReward", 5);
  // LB-283: spelling was the ONLY game seeded without a pass reward, which is why the wizard
  // summary showed it as a flat number while vocab, times tables and maths all showed a range.
  // migrateState backfilled it on the next load, so the value appeared later — the wizard was
  // just where the gap was visible. Same 3:5 ratio as its siblings.
  seed("spellingPassReward", 3);
  seed("vocabPassReward", 3);
  seed("vocabFullReward", 5);
  seed("ttStreakPassReward", 3);
  seed("ttStreakFullReward", 5);
  seed("mathsPassReward", 3);
  seed("mathsFullReward", 5);
  // A child added outside the wizard gets the same starter tasks as one added inside it —
  // otherwise the second child in a household opens the app with nothing to earn from while
  // their sibling has two. Priced from THIS child's cap, so a £3/week sibling doesn't
  // inherit a £5/week child's numbers.
  // Order matters (LB-132): the flat seeds above only establish which games are configured.
  // seedStandardSet then prices them from this child's cap and overwrites those defaults —
  // it must run last, or the flat values win and the child is under cap again.
  seedStandardSet(state, childId);
  return state;
}
